import { isDefined, noop } from '@sgme/fp';
import classNames from 'classnames';
import classnames from 'classnames';
import { CurrencySymbol } from 'components/shared/NewAmountInput/CurrencySymbol';
import { HighlightColor } from 'components/shared/NumberFormatter/NumberFormatter.model';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownProps, DropdownToggle } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { ContentPosition } from 'types/content-position';
import { useToggle } from 'usehooks-ts';

export interface WithUnitProps {
  children?: ReactNode;
  appendedChildren?: ReactNode;
  className?: string;
  unitClassName?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  unit?: string;
  units?: string[];
  unitsDirection?: DropdownProps['drop'];
  onUnitSelected?: (unit: string) => void;
  e2e?: string;
  minWidth?: string;
  maxWidth?: string;
  unitE2e?: string;

  title?: string;

  showUnit?: boolean;

  unitsPosition?: Extract<ContentPosition, 'start' | 'end'>;
}

export const WithUnit: FC<WithUnitProps> = ({
  className,
  unitClassName,
  hasError,
  isDisabled,
  isReadOnly,
  e2e,
  children,
  units,
  unitE2e,
  unit: selectedUnit,
  appendedChildren,
  minWidth,
  maxWidth,
  title,
  showUnit = true,
  onUnitSelected = noop,
  unitsDirection = 'end',
  unitsPosition = 'end',
}: WithUnitProps) => {
  const [isOpen, toggle] = useToggle();
  const unitElement =
    isDefined(units) && units.length > 1 ? (
      <Dropdown show={isOpen} onToggle={toggle} drop={unitsDirection}>
        <DropdownToggle className="input-group-text" id="unitsBtn" data-e2e={unitE2e ?? 'unit'} variant="secondary">
          <CurrencySymbol code={selectedUnit || ''} />
        </DropdownToggle>
        <DropdownMenu>
          {units
            .filter(unit => unit !== selectedUnit)
            .map(unit => (
              <DropdownItem key={unit} onClick={() => onUnitSelected(unit)}>
                <CurrencySymbol code={unit} />
              </DropdownItem>
            ))}
        </DropdownMenu>
      </Dropdown>
    ) : (
      selectedUnit && (
        <span className="input-group-text" data-e2e={unitE2e ?? 'unit'}>
          <CurrencySymbol code={selectedUnit} />
        </span>
      )
    );
  return (
    <div
      className={classnames('with-unit-component', 'input-group', className, unitClassName, {
        'is-warning': hasError,
        disabled: isDisabled,
        readonly: isReadOnly,
      })}
      title={title}
      style={{ minWidth, maxWidth }}
      data-e2e={`${e2e}-with-unit`}
    >
      {showUnit && unitsPosition === 'start' && unitElement}
      {children}
      {showUnit && unitsPosition === 'end' && unitElement}
      {appendedChildren}
    </div>
  );
};

export const WithUnitSkeleton: FC<PropsWithChildren<{ unit?: string }>> = ({ children, unit }) => (
  <div className="with-unit-component input-group">
    {children}
    <span className="unit input-group-text">
      <Skeleton width={`${unit?.length ?? 1}em`} />
    </span>
  </div>
);

type Props<T> = T &
  WithUnitProps & {
    UnitComponent: FC<T>;
    isHighlighted?: HighlightColor;
  };
export function WithUnitComponent<T>({ ...props }: Props<T>) {
  return (
    <WithUnit
      appendedChildren={props.appendedChildren}
      e2e={props.e2e}
      hasError={props.hasError}
      isDisabled={props.isDisabled}
      isReadOnly={props.isReadOnly}
      maxWidth={props.maxWidth}
      minWidth={props.minWidth}
      onUnitSelected={props.onUnitSelected}
      showUnit={props.showUnit}
      title={props.title}
      unit={props.unit}
      unitClassName={props.unitClassName}
      unitE2e={props.unitE2e}
      units={props.units}
      unitsDirection={props.unitsDirection}
      unitsPosition={props.unitsPosition}
      className={classNames(props.className, 'percent-with-unit-component', {
        'is-highlighted-success': props.isHighlighted === 'success',
        'is-highlighted-info': props.isHighlighted === 'info',
        'is-highlighted-turquoise': props.isHighlighted === 'turquoise',
      })}
    >
      <props.UnitComponent {...props} />
    </WithUnit>
  );
}
